"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { blurImageProps } from "@/lib/image-blur";
import { cn } from "@/lib/cn";

export type Certificate = {
  src: string;
  alt: string;
  /** Short caption under the badge (e.g. "Qualitätsmanagement"). */
  label: string;
};

/**
 * Strip of certificate / award badges. Used on /ueber-isg and
 * /versicherung-qualitaet to surface the TÜV ISO 9001 + 14001, EcoZert,
 * and Focus Wachstums-Champion seals as social proof.
 *
 * Each badge opens a lightbox so the certificate document is readable in
 * full size (click, Escape / backdrop to close, arrows to browse).
 *
 * Visual language matches the rest of the site: white background, light
 * border-y separators, headline left-aligned with kicker, monochrome badges
 * rendered with `object-contain` on white so corporate logos stay legible.
 */
export function CertificateRow({
  eyebrow,
  title,
  sub,
  items,
  dense = false,
}: {
  eyebrow?: string;
  title?: string;
  sub?: string;
  items: Certificate[];
  /** Kompakt (Ads-Landingpage direkt unter dem Hero): mobil 3 Spalten,
   *  weniger Innenabstand, damit die Siegel nicht einen ganzen Bildschirm
   *  vor den Preisen belegen. */
  dense?: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (dir: 1 | -1) =>
      setOpen((cur) => (cur === null ? cur : (cur + dir + items.length) % items.length)),
    [items.length],
  );

  // Escape closes, arrow keys browse; body scroll locked while open.
  useEffect(() => {
    if (open === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close, step]);

  const active = open === null ? null : items[open];

  return (
    <section aria-label="Zertifikate & Auszeichnungen" className="border-y border-line-200 bg-canvas">
      <div className={cn("container", dense ? "py-10 md:py-14" : "py-14 md:py-20")}>
        <header className="max-w-3xl">
          {eyebrow ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">
              {eyebrow}
            </p>
          ) : null}
          {title ? (
            <h2 className="mt-5 text-[1.75rem] font-semibold leading-[1.08] tracking-[-0.02em] md:text-[2.25rem] lg:text-[2.5rem]">
              {title}
            </h2>
          ) : null}
          {sub ? (
            <p className="mt-6 max-w-[60ch] text-base leading-relaxed text-ink-700 md:text-lg">
              {sub}
            </p>
          ) : null}
        </header>

        <ul
          className={cn(
            "grid lg:grid-cols-5",
            dense
              ? "mt-8 grid-cols-3 gap-2.5 sm:gap-4 md:mt-10 md:gap-6"
              : "mt-12 grid-cols-2 gap-6 sm:grid-cols-3 md:gap-8",
          )}
        >
          {items.map((c, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => setOpen(i)}
                aria-label={`${c.alt} — Zertifikat groß ansehen`}
                className={cn(
                  "flex w-full cursor-zoom-in flex-col items-center rounded-xl border border-line-200 bg-canvas text-center transition-colors hover:border-ink-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-900/30",
                  dense ? "h-full p-2.5 sm:p-4 md:p-5" : "p-5",
                )}
              >
                <div className="relative aspect-[3/4] w-full">
                  <Image
                    src={c.src}
                    {...blurImageProps(c.src)}
                    alt={c.alt}
                    fill
                    className="object-contain"
                    sizes="(max-width: 640px) 45vw, 220px"
                  />
                </div>
                <p
                  className={cn(
                    "max-w-[20ch] font-medium leading-snug text-ink-900",
                    dense ? "mt-2.5 text-[11px] sm:mt-4 sm:text-sm" : "mt-5 text-sm",
                  )}
                >
                  {c.label}
                </p>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Lightbox — Großansicht des Zertifikats */}
      {active ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={active.alt}
          onClick={close}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-900/80 p-4 backdrop-blur-sm md:p-10"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Schließen"
            className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>

          {items.length > 1 ? (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  step(-1);
                }}
                aria-label="Vorheriges Zertifikat"
                className="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:left-6"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  step(1);
                }}
                aria-label="Nächstes Zertifikat"
                className="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:right-6"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          ) : null}

          <figure
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-full w-full max-w-3xl flex-col items-center"
          >
            <div className="relative h-[70vh] w-full overflow-hidden rounded-lg bg-white md:h-[78vh]">
              <Image
                src={active.src}
                alt={active.alt}
                fill
                className="object-contain p-3 md:p-5"
                sizes="(max-width: 768px) 92vw, 768px"
                priority
              />
            </div>
            <figcaption className="mt-4 text-center text-sm font-medium text-white/90">
              {active.label}
            </figcaption>
          </figure>
        </div>
      ) : null}
    </section>
  );
}
